<template>
    <div class="radio-typeahead">
        <span class="radio-typeahead__label-with-tooltip">
            <label
                :for="inputId"
                class="radio-typeahead__label"
                >Add a Radio</label
            >
            <info-tooltip>
                Search by serial number to add a radio. If a radio has been assigned to another relay, it must be
                removed from that relay before it can be selected.
            </info-tooltip>
        </span>

        <typeahead-field
            :object-types="objectTypes"
            view="relay_radio_typeahead"
            :filters="availabilityFilters"
            :exclude-urns="excludeUrns"
            :row-status="statusOf"
            :disabled="disabled"
            :input-id="inputId"
            :validation-message="validationMessage"
            :data-test-id-prefix="dataTestIdPrefix"
            clear-on-select
            placeholder="Add a Radio by Serial"
            :empty-search-message="emptySearchMessage"
            @select="onSelect"
        >
            <!--
                The row is the control, not a <label for>, which would focus the
                checkbox and blur the field. mousedown.prevent keeps focus in the input.
            -->
            <template #overlay-header>
                <div
                    class="radio-typeahead__toggle"
                    role="checkbox"
                    tabindex="0"
                    :aria-checked="String(showAll)"
                    :data-test-id="`${dataTestIdPrefix}-show-all`"
                    @mousedown.prevent
                    @click="showAll = !showAll"
                    @keydown.space.prevent="showAll = !showAll"
                >
                    <input
                        type="checkbox"
                        :checked="showAll"
                        tabindex="-1"
                        aria-hidden="true"
                        :data-test-id="`${dataTestIdPrefix}-show-all-toggle`"
                    />
                    <span>Include unavailable radios</span>
                </div>
            </template>
        </typeahead-field>
    </div>
</template>

<script>
import InfoTooltip from '@/components/InfoTooltip.vue'
import TypeaheadField from '@/components/Typeahead/TypeaheadField.vue'
import radioSuggestionStatus from '@/utils/radioSuggestionStatus'

const RADIO_OBJECT_TYPE = 'asset_radio'

export default {
    name: 'RadioTypeahead',

    components: {
        InfoTooltip,
        TypeaheadField,
    },

    props: {
        disabled: {
            required: false,
            type: Boolean,
            default: false,
        },
        selectedUrns: {
            required: false,
            type: Array,
            default: () => [],
        },
        // Distinguishes a radio still attached to this relay from one on another relay.
        currentAssemblyId: {
            required: false,
            type: Number,
            default: null,
        },
        // Native validity message for the field; empty means valid.
        validationMessage: {
            required: false,
            type: String,
            default: '',
        },
        dataTestIdPrefix: {
            required: true,
            type: String,
        },
    },

    emits: ['add'],

    data() {
        return {
            showAll: false,
        }
    },

    computed: {
        objectTypes: () => [RADIO_OBJECT_TYPE],

        inputId() {
            return `${this.dataTestIdPrefix}-input`
        },

        availabilityFilters() {
            if (this.showAll) {
                return []
            }

            return [{ field: 'is_attached_to_relay', operator: 'eq', value: false }]
        },

        excludeUrns() {
            return this.showAll ? [] : this.selectedUrns
        },

        emptySearchMessage() {
            return this.showAll ? 'No radio matches that serial' : 'No available radio matches that serial'
        },
    },

    methods: {
        onSelect(row) {
            this.$emit('add', row)
        },

        /**
         * @param {object} row
         * @returns {{state: string, label: string}}
         */
        statusOf(row) {
            return radioSuggestionStatus(row, {
                currentAssemblyId: this.currentAssemblyId,
                selectedUrns: this.selectedUrns,
            })
        },
    },
}
</script>

<style lang="scss" scoped>
.radio-typeahead {
    &__label-with-tooltip {
        display: flex;
        align-items: center;
        gap: 6px;
    }

    &__label {
        margin-bottom: 10px;
    }

    // Sits in PrimeVue's light overlay, so uses PrimeVue colours, not the dark page's.
    &__toggle {
        display: flex;
        align-items: center;
        gap: 7px;
        cursor: pointer;
        user-select: none;
        border-radius: 3px;

        &:focus-visible {
            outline: 2px solid var(--p-primary-color);
            outline-offset: 3px;
        }

        // Display only, so a click toggles the row once and never focuses the checkbox.
        input {
            width: auto;
            height: auto;
            margin: 0;
            pointer-events: none;
        }

        span {
            font-family: 'Avenir Book';
            font-size: 0.8rem;
            color: var(--p-text-color, #212628);
        }
    }
}
</style>
